import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getCustomer, createCustomer, updateCustomer } from '../services/api'
import { GST_STATES } from '../lib/formatters'
import { stateCodeFromAddress } from '../lib/gst'
import { useAuth } from '../contexts/AuthContext'

export default function CustomerForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { isInvoiceCreator } = useAuth()
  const isEdit = !!id

  const [form, setForm] = useState({
    name: '', type: 'OTHER', gstin: '', pan: '', gstStateCode: '',
    email: '', phone: '', billingAddress: '', shippingAddress: '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [stateOverrideReason, setStateOverrideReason] = useState('')

  useEffect(() => {
    if (isEdit) {
      getCustomer(id).then((res) => {
        const c = res.data
        setForm({
          name: c.name, type: c.type, gstin: c.gstin || '', pan: c.pan || '',
          gstStateCode: c.gstStateCode?.toString() || '', email: c.email || '',
          phone: c.phone || '', billingAddress: c.billingAddress || '', shippingAddress: c.shippingAddress || '',
        })
      }).catch(() => navigate('/customers'))
    }
  }, [id, isEdit, navigate])

  const handleGstinChange = (gstin) => {
    const upper = gstin.toUpperCase()
    setForm((f) => ({
      ...f, gstin: upper,
      gstStateCode: upper.length >= 2 ? upper.slice(0, 2) : f.gstStateCode,
    }))
  }

  // Where the address itself says the customer is. A state picked in the dropdown that
  // contradicts this is how O3D-2627-0176 came to charge IGST inside Karnataka.
  // A GSTIN settles the state, so the backend skips the check there and so must this —
  // otherwise the form tells the user to change a state code that is provably right.
  const addressState = form.gstin?.trim() ? null : stateCodeFromAddress(form.billingAddress)
  const stateClash = addressState && form.gstStateCode
    && Number(form.gstStateCode) !== addressState

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSaving(true)
    try {
      // Strip placeholder values (0, -, –, NA, etc.) from GSTIN and PAN
      const cleanGstin = form.gstin && /^[0\-–—.NA\/\\]+$/i.test(form.gstin.trim()) ? '' : form.gstin.trim()
      const cleanPan = form.pan && /^[0\-–—.NA\/\\]+$/i.test(form.pan.trim()) ? '' : form.pan.trim()
      const payload = {
        ...form,
        gstin: cleanGstin || '',
        pan: cleanPan || '',
        gstStateCode: form.gstStateCode ? Number(form.gstStateCode) : null,
        // Only sent when the user has said why the state and the address differ. Without
        // it the server refuses the save and the form has no way to clear the refusal.
        ...(stateOverrideReason.trim() ? { stateOverrideReason: stateOverrideReason.trim() } : {}),
      }
      if (isEdit) {
        await updateCustomer(id, payload)
        navigate(`/customers/${id}`)
      } else {
        const res = await createCustomer(payload)
        // Invoice-creator can't view customer pages — send them back to raise the invoice.
        navigate(isInvoiceCreator ? '/invoices/new' : `/customers/${res.data.id}`)
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save customer')
    } finally {
      setSaving(false)
    }
  }

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value })

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-2xl font-semibold text-gray-900">{isEdit ? 'Edit Customer' : 'New Customer'}</h1>

      <form onSubmit={handleSubmit} className="card space-y-4">
        {error && <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">Name *</label>
            <input className="input" value={form.name} onChange={set('name')} required />
          </div>
          <div>
            <label className="label">Type</label>
            <select className="select" value={form.type} onChange={set('type')}>
              <option value="DOCTOR">Doctor</option>
              <option value="HOSPITAL">Hospital</option>
              <option value="PATIENT">Patient</option>
              <option value="OTHER">Other</option>
            </select>
          </div>
          <div>
            <label className="label">GSTIN</label>
            <input className="input font-mono" value={form.gstin} onChange={(e) => handleGstinChange(e.target.value)} maxLength={15} placeholder="29AAAAA0000A1Z5" />
          </div>
          <div>
            <label className="label">PAN</label>
            <input className="input font-mono" value={form.pan} onChange={set('pan')} maxLength={10} placeholder="AAAAA0000A" />
          </div>
          <div>
            <label className="label">State (Place of Supply) <span className="text-red-600">*</span></label>
            <select className="select" value={form.gstStateCode} onChange={set('gstStateCode')}>
              <option value="">Select state</option>
              {GST_STATES.map((s) => <option key={s.code} value={s.code}>{s.code} - {s.name}</option>)}
            </select>
            {stateClash && (
              <>
                <p className="mt-1 text-xs text-red-600">
                  The billing address reads{' '}
                  {GST_STATES.find((s) => s.code === addressState)?.name || ''}. The state
                  decides the GST on every invoice for this customer, so the two cannot
                  disagree — fix whichever is wrong.
                </p>
                <input
                  className="input mt-2 text-xs"
                  value={stateOverrideReason}
                  onChange={(e) => setStateOverrideReason(e.target.value)}
                  placeholder="Or say why they differ, in at least 10 characters"
                />
              </>
            )}
            {!form.gstStateCode && (
              <p className="mt-1 text-xs text-amber-700">
                Set this and every invoice for this customer gets the right GST automatically.
                Leave it blank and the place of supply must be picked by hand each time.
              </p>
            )}
          </div>
          <div>
            <label className="label">Email</label>
            <input className="input" value={form.email} onChange={set('email')} placeholder="email@example.com" />
          </div>
          <div>
            <label className="label">Phone</label>
            <input className="input" value={form.phone} onChange={set('phone')} />
          </div>
        </div>
        <div>
          <label className="label">Billing Address</label>
          <textarea className="input" rows={2} value={form.billingAddress} onChange={set('billingAddress')} />
        </div>
        <div>
          <label className="label">Shipping Address</label>
          <textarea className="input" rows={2} value={form.shippingAddress} onChange={set('shippingAddress')} />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={() => navigate(-1)} className="btn-secondary">Cancel</button>
          <button type="submit" disabled={saving} className="btn-primary">{saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}</button>
        </div>
      </form>
    </div>
  )
}
